if (!customElements.get('luma-guided-parts')) {
  const focusAfterSelection = new Set();
  customElements.define('luma-guided-parts', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.native = this.querySelector('[data-native-parts]');
      this.config = JSON.parse(this.querySelector('[data-parts-config]').textContent);
      this.details = this.querySelector('details');
      this.trigger = this.querySelector('summary');
      this.search = this.querySelector('[data-parts-search]');
      this.list = this.querySelector('[data-parts-list]');
      this.records = [...this.native.options].map(option => ({ option, name: option.value, ...(this.config.parts[option.value] || { category: this.config.labels.other }) }));
      const selected = this.records.find(record => record.option.selected);
      this.groups = { camera: selected?.category === this.config.labels.cameraCategory, general: selected?.category !== this.config.labels.cameraCategory, antenna: selected?.model === this.config.labels.antennaGroup };
      if (this.config.groups) {
        this.groups = {};
        const initialise = groups => groups.forEach(group => {
          this.groups[group.id] = selected?.category === group.id || group.children?.some(child => child.id === selected?.category) || false;
          if (group.children) initialise(group.children);
        });
        initialise(this.config.groups);
      }
      this.querySelector('[data-parts-current]').textContent = selected?.name || this.config.labels.choose;
      this.querySelector('[data-part-summary]').textContent = selected?.name || '';
      this.render();
      this.setAttribute('data-ready', '');
      this.initialised = true;
      this.onInput = () => this.render();
      this.onChange = event => { if (event.target !== this.native) event.stopPropagation(); };
      this.onToggle = () => { if (this.details.open) this.search.focus({ preventScroll: true }); };
      this.onOutside = event => { if (!event.composedPath().includes(this)) this.details.open = false; };
      this.onKey = event => {
        if (event.key === 'Escape' && this.details.open) {
          event.preventDefault(); event.stopPropagation(); this.details.open = false; this.trigger.focus({ preventScroll: true });
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          const buttons = [...this.list.querySelectorAll('button')];
          const index = buttons.indexOf(event.target);
          if (event.target === this.search || index >= 0) {
            event.preventDefault();
            buttons[Math.max(0, Math.min(buttons.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus();
          }
        }
      };
      this.search.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKey);
      this.details.addEventListener('toggle', this.onToggle);
      document.addEventListener('click', this.onOutside);
      if (focusAfterSelection.has(this.native.id)) {
        focusAfterSelection.delete(this.native.id);
        this.trigger.focus({ preventScroll: true });
      }
    }
    disconnectedCallback() {
      this.search?.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKey);
      this.details?.removeEventListener('toggle', this.onToggle);
      document.removeEventListener('click', this.onOutside);
      this.initialised = false;
    }
    choose(record) {
      this.details.open = false;
      this.trigger.focus({ preventScroll: true });
      if (this.native.value === record.name) return;
      focusAfterSelection.add(this.native.id);
      this.native.value = record.name;
      this.native.dispatchEvent(new Event('change', { bubbles: true }));
    }
    choice(record, parent) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'luma-parts-choice';
      button.setAttribute('aria-pressed', String(record.option.selected));
      const name = document.createElement('span'); name.textContent = record.name; button.append(name);
      if (record.option.dataset.available === 'false') {
        const status = document.createElement('small'); status.textContent = this.config.labels.soldOut; button.append(status);
      }
      if (record.option.selected) {
        const check = document.createElement('span'); check.textContent = '✓'; check.setAttribute('aria-hidden', 'true'); button.append(check);
      }
      button.addEventListener('click', () => this.choose(record)); parent.append(button);
    }
    group(title, key, records, parent, renderChildren) {
      if (!records.length) return;
      const button = document.createElement('button'); button.type = 'button'; button.className = 'luma-parts-group';
      const id = `${this.native.id}-${key}`;
      button.setAttribute('aria-expanded', String(this.groups[key])); button.setAttribute('aria-controls', id);
      const label = document.createElement('span'); label.textContent = title;
      const count = document.createElement('small'); count.textContent = records.length;
      const arrow = document.createElement('span'); arrow.textContent = this.groups[key] ? '−' : '+'; arrow.setAttribute('aria-hidden', 'true');
      button.append(label, count, arrow); parent.append(button);
      const children = document.createElement('div'); children.id = id; children.hidden = !this.groups[key]; parent.append(children);
      if (this.groups[key]) renderChildren(children);
      button.addEventListener('click', () => {
        const scrollTop = this.list.scrollTop;
        this.groups[key] = !this.groups[key]; this.render();
        this.list.scrollTop = scrollTop;
        this.list.querySelector(`[aria-controls="${id}"]`)?.focus({ preventScroll: true });
      });
    }
    render() {
      this.list.replaceChildren();
      const query = this.search.value.toLocaleLowerCase().trim();
      const words = query.split(/\s+/).filter(Boolean);
      const records = this.records.filter(record => words.every(word => record.name.toLocaleLowerCase().includes(word)));
      if (this.config.groups) {
        this.querySelector('[data-parts-count]').textContent = `${records.length} ${query ? this.config.labels.searchResults : this.config.labels.options}`;
        const renderGroups = (groups, parent) => groups.forEach(group => {
          const ids = group.children ? group.children.map(child => child.id) : [group.id];
          const matches = records.filter(record => ids.includes(record.category));
          if (!matches.length) return;
          if (query) {
            if (group.children) renderGroups(group.children, parent);
            else {
              const heading = document.createElement('div'); heading.className = 'luma-parts-results-heading'; heading.textContent = group.label; parent.append(heading);
              matches.forEach(record => this.choice(record, parent));
            }
          } else this.group(group.label, group.id, matches, parent, children => {
            if (group.children) {
              const nested = document.createElement('div'); nested.className = 'luma-parts-nested'; children.append(nested);
              renderGroups(group.children, nested);
            } else matches.forEach(record => this.choice(record, children));
          });
        });
        renderGroups(this.config.groups, this.list);
        if (!records.length) { const empty = document.createElement('div'); empty.className = 'luma-parts-empty'; empty.textContent = this.config.labels.empty; this.list.append(empty); }
        return;
      }
      const camera = records.filter(record => record.category === this.config.labels.cameraCategory);
      const general = records.filter(record => record.category !== this.config.labels.cameraCategory);
      this.querySelector('[data-parts-count]').textContent = `${records.length} ${query ? this.config.labels.searchResults : this.config.labels.options}`;
      if (query) {
        for (const [label, group] of [[this.config.labels.cameraCategory, camera], [this.config.labels.other, general]]) {
          if (!group.length) continue;
          const heading = document.createElement('div'); heading.className = 'luma-parts-results-heading'; heading.textContent = label; this.list.append(heading);
          group.forEach(record => this.choice(record, this.list));
        }
        if (!records.length) { const empty = document.createElement('div'); empty.className = 'luma-parts-empty'; empty.textContent = this.config.labels.empty; this.list.append(empty); }
      } else {
        this.group(this.config.labels.cameraCategory, 'camera', camera, this.list, parent => camera.forEach(record => this.choice(record, parent)));
        this.group(this.config.labels.other, 'general', general, this.list, parent => {
          const antenna = general.filter(record => record.model === this.config.labels.antennaGroup);
          let rendered = false;
          general.forEach(record => {
            if (antenna.includes(record)) {
              if (!rendered) {
                rendered = true;
                const nested = document.createElement('div'); nested.className = 'luma-parts-nested'; parent.append(nested);
                this.group(this.config.labels.antennaGroup, 'antenna', antenna, nested, sub => antenna.forEach(item => this.choice(item, sub)));
              }
            } else this.choice(record, parent);
          });
        });
      }
    }
  });
}
